import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Tooltip,
    LinearProgress,
    Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import { getUserRole } from "../utils/Auth";
import {
    getAdvancePayments,
    addAdvancePayment,
    deleteAdvancePayment,
} from "../hooks/advancePaymentsApi";
import { useTheme } from "../context/ThemeContext"; // ⬅️ ugyanúgy, mint TaskDetailsModal
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdvancePaymentsModal = ({ order, open, onClose, onAdvanceUpdated }) => {
    const [payments, setPayments] = useState([]);
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem("token");
    const userRole = getUserRole();
    const { darkMode } = useTheme(); // ⬅️ theme hook
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);

    const fetchPayments = async () => {
        if (!order) return;
        try {
            const data = await getAdvancePayments(order.id, token);
            setPayments(data.payments || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (open && order) fetchPayments();
    }, [open, order]);

    const handleAdd = async () => {
        if (!order || !amount || isNaN(amount)) return;
        setLoading(true);
        try {
            await addAdvancePayment(order.id, parseFloat(amount), note, token);
            setAmount("");
            setNote("");
            await fetchPayments();
            // 🔥 Jelentjük vissza az új százalékot
            if (typeof onAdvanceUpdated === "function") {
                const data = await getAdvancePayments(order.id, token);
                const totalPaid = (data.payments || []).reduce((sum, p) => sum + p.amount, 0);
                const newPercent = order.totalPrice
                    ? Math.min((totalPaid / order.totalPrice) * 100, 100)
                    : 0;
                onAdvanceUpdated(newPercent);
            }
            toast.success("Plată de avans adăugată cu succes!", { toastId: "addSuccess" });

        } catch (err) {
            console.error(err);
            toast.error(err.message || "Eroare la adăugarea plății de avans.", {
                toastId: "addError",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        setSelectedPaymentId(id);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!selectedPaymentId) return;

        try {
            await deleteAdvancePayment(selectedPaymentId, token);
            await fetchPayments();

            // 🔥 Frissítjük a százalékot is
            if (typeof onAdvanceUpdated === "function") {
                const data = await getAdvancePayments(order.id, token);
                const totalPaid = (data.payments || []).reduce((sum, p) => sum + p.amount, 0);
                const newPercent = order.totalPrice
                    ? Math.min((totalPaid / order.totalPrice) * 100, 100)
                    : 0;
                onAdvanceUpdated(newPercent);
            }
            toast.success("Plata a fost ștearsă.", { toastId: "deleteSuccess" });
        } catch (err) {
            console.error(err);
            toast.error("Eroare la ștergerea plății.", { toastId: "deleteError" });
        } finally {
            setDeleteModalOpen(false);
            setSelectedPaymentId(null);
        }
    };

    if (!order) return null;

    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const completion = order.totalPrice
        ? Math.min(Math.round((totalPaid / order.totalPrice) * 100), 100)
        : 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: darkMode
                        ? "linear-gradient(to bottom right, #111827, #1e293b)"
                        : "linear-gradient(to bottom right, #ffffff, #f8fafc)",
                    color: darkMode ? "#e2e8f0" : "#1e293b",
                    border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                    boxShadow: darkMode
                        ? "0 0 25px rgba(0,0,0,0.7)"
                        : "0 0 25px rgba(0,0,0,0.15)",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: darkMode ? "1px solid #374151" : "1px solid #e2e8f0",
                    pb: 1,
                }}
            >
                <div>
                    <h2
                        style={{
                            fontWeight: 600,
                            color: darkMode ? "#60a5fa" : "#2563eb",
                        }}
                    >
                        Plăți de avans — Comanda #{order.orderNumber || "-"}
                    </h2>
                    <p
                        style={{
                            fontSize: 14,
                            color: darkMode ? "#94a3b8" : "#475569",
                            marginTop: 4,
                        }}
                    >
                        Total comandă:{" "}
                        <b style={{ color: darkMode ? "#f1f5f9" : "#111827" }}>
                            {order.totalPrice?.toFixed(2) ?? 0} €
                        </b>
                    </p>
                </div>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: darkMode ? "#9ca3af" : "#64748b",
                        "&:hover": {
                            color: darkMode ? "#f87171" : "#dc2626",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ border: "none" }}>
                <div className="mb-4 space-y-1">
                    <div
                        className={`flex justify-between text-sm ${darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                    >
                        <span>Total plătit:</span>
                        <span> {totalPaid.toFixed(2)} €</span>
                    </div>

                    {order.totalPrice && (
                        <div
                            className={`flex justify-between text-sm font-medium ${darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                        >
                            <span>Rest de plată:</span>
                            <span
                                className={`${totalPaid >= order.totalPrice
                                    ? darkMode
                                        ? "text-green-400"
                                        : "text-green-600"
                                    : darkMode
                                        ? "text-yellow-400"
                                        : "text-orange-600"
                                    }`}
                            >
                                {(order.totalPrice - totalPaid).toFixed(2)} €
                            </span>
                        </div>
                    )}

                    <div style={{ position: "relative", marginTop: 4 }}>
                        <LinearProgress
                            variant="determinate"
                            value={completion}
                            sx={{
                                height: 10,
                                borderRadius: 4,
                                backgroundColor: darkMode ? "#1f2937" : "#e5e7eb",
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor:
                                        completion === 100
                                            ? "#22c55e"
                                            : darkMode
                                                ? "#3b82f6"
                                                : "#2563eb",
                                },
                            }}
                        />
                        <span
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: 12,
                                fontWeight: 600,
                                color: darkMode ? "#f1f5f9" : "#1e293b",
                                textShadow: darkMode
                                    ? "0 0 4px rgba(0,0,0,0.8)"
                                    : "0 0 3px rgba(255,255,255,0.6)",
                                userSelect: "none",
                            }}
                        >
                            {completion}%
                        </span>
                    </div>

                </div>


                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        borderColor: darkMode ? "#374151" : "#e5e7eb",
                        backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                    }}
                >
                    <table className="w-full text-sm">
                        <thead
                            className={
                                darkMode
                                    ? "bg-[#111827] text-gray-300"
                                    : "bg-gray-100 text-gray-700"
                            }
                        >
                            <tr>
                                <th className="text-left py-2 px-3">Dată</th>
                                <th className="text-left py-2 px-3">Sumă (€)</th>
                                <th className="text-left py-2 px-3">Observații</th>
                                {userRole === "Admin" && <th className="w-10"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {payments.length > 0 ? (
                                payments.map((p) => (
                                    <tr
                                        key={p.id}
                                        className={`border-t ${darkMode
                                            ? "border-gray-700 hover:bg-gray-800/50"
                                            : "border-gray-200 hover:bg-gray-50"
                                            } transition-colors`}
                                    >
                                        <td className="py-2 px-3">
                                            {new Date(p.paymentDate).toLocaleDateString()}
                                        </td>
                                        <td className="py-2 px-3 font-medium">
                                            {p.amount.toFixed(2)} €
                                        </td>
                                        <td className="py-2 px-3">{p.note || "—"}</td>
                                        {userRole === "Admin" && (
                                            <td className="text-center">
                                                <Tooltip title="Șterge plată">
                                                    <IconButton
                                                        onClick={() => handleDelete(p.id)}
                                                        size="small"
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="4"
                                        className={`italic text-center py-3 ${darkMode ? "text-gray-500" : "text-gray-400"
                                            }`}
                                    >
                                        Nicio plată de avans înregistrată.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Paper>

                <div className="mt-5 flex flex-col sm:flex-row gap-3 items-center">
                    <TextField
                        label="Sumă (€)"
                        type="number"
                        size="small"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        sx={{
                            width: { xs: "100%", sm: 150 }, // ⬅️ csak 130px széles
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: darkMode ? "#0f172a" : "#f9fafb",
                                color: darkMode ? "#e2e8f0" : "#111827",
                                "& fieldset": { borderColor: darkMode ? "#475569" : "#cbd5e1" },
                                "&:hover fieldset": { borderColor: darkMode ? "#60a5fa" : "#2563eb" },
                                "&.Mui-focused fieldset": { borderColor: darkMode ? "#3b82f6" : "#2563eb" },
                            },
                        }}
                    />

                    <TextField
                        label="Observații"
                        size="small"
                        fullWidth
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: darkMode ? "#0f172a" : "#f9fafb",
                                color: darkMode ? "#e2e8f0" : "#111827",
                                "& fieldset": { borderColor: darkMode ? "#475569" : "#cbd5e1" },
                                "&:hover fieldset": { borderColor: darkMode ? "#60a5fa" : "#2563eb" },
                                "&.Mui-focused fieldset": { borderColor: darkMode ? "#3b82f6" : "#2563eb" },
                            },
                        }}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddCircleIcon />}
                        onClick={handleAdd}
                        disabled={loading}
                        sx={{
                            width: { xs: "100%", sm: 150 }, // ⬅️ csak 130px széles
                            height: 40,
                            textTransform: "none",
                            fontWeight: 500,
                            backgroundColor: darkMode ? "#3b82f6" : "#2563eb",
                            whiteSpace: "nowrap", // ⬅️ ne törjön meg az “Adaugă” szöveg
                            "&:hover": { backgroundColor: darkMode ? "#2563eb" : "#1d4ed8" },
                        }}
                    >
                        Adaugă
                    </Button>
                </div>

            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: darkMode ? "1px solid #374151" : "1px solid #e2e8f0",
                    justifyContent: "flex-end",
                    pt: 1.5,
                    pr: { xs: 0, sm: "22px" }, // ⬅️ pont annyi, mint az Adaugă gomb + gap
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        width: { xs: "100%", sm: 150 },
                        height: 40,
                        textTransform: "none",
                        fontWeight: 500,
                        borderColor: darkMode ? "#475569" : "#cbd5e1",
                        color: darkMode ? "#e2e8f0" : "#475569",
                        "&:hover": {
                            borderColor: darkMode ? "#60a5fa" : "#94a3b8",
                            color: darkMode ? "#60a5fa" : "#1e293b",
                        },
                    }}
                >
                    Închide
                </Button>
            </DialogActions>
            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                entityName="plată de avans"
                message="Această acțiune nu poate fi anulată."
            />
            <ToastContainer
                position="bottom-center"
                autoClose={2500}
                hideProgressBar={false}
                closeOnClick
                pauseOnHover
                draggable
                theme={darkMode ? "dark" : "light"}
                toastStyle={
                    darkMode
                        ? {
                            background: "#1f2937", // sötétszürke (nem fekete)
                            color: "#f1f5f9",       // világosszürke szöveg
                            border: "1px solid #374151",
                            boxShadow: "0 0 12px rgba(0,0,0,0.6)",
                        }
                        : {
                            background: "#ffffff",
                            color: "#1e293b",
                        }
                }
                style={{ zIndex: 999999 }}
            />

        </Dialog>
    );
};

export default AdvancePaymentsModal;
